/**
 * Settings > Agents > Approvals and events (firm-wide).
 *
 * Firm switches the assistant's approval policy reads:
 *
 * - **Ask before every change** (`agent_ask_every_write`). By default a
 *   reversible change runs and can be undone from its change card; anything
 *   that reaches the client or cannot be undone always asks. With this on,
 *   every change asks first.
 * - **Go ahead on Recommended answers** (`agent_auto_answer_minutes`). Off by
 *   default. When an attorney has said not to be asked first, a question the
 *   assistant asks anyway goes with its Recommended answers after
 *   {@link AUTO_ANSWER_MINUTES} minutes, and the reply says so.
 * - **Wake the assistant on events** (`agent_event_wakes`), one switch per
 *   client event. When one is on, that event posts in the client's channel
 *   and the assistant proposes next steps; every proposal waits for
 *   approval and the client's assignees are told.
 *
 * Everyone at the firm sees the settings; owners and admins change them. A
 * switch saves as soon as it flips (optimistic, reverted on failure).
 */
import { Loader2 } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { LawFirmsAPI } from "@/api/lawFirms";
import type { AgentEventKind, LawFirm } from "@/api/types/lawFirm";
import { Switch } from "@/components/ui/switch";
import { announceFirmUpdated } from "@/features/lawFirms/firmEvents";

type Props = {
  lawFirmId: string | null;
  /** Owner / admin (or platform superadmin): may flip the switches. */
  canEdit: boolean;
};

export const EVENT_WAKE_ROWS: ReadonlyArray<{
  kind: AgentEventKind;
  label: string;
  caption: string;
}> = [
  {
    kind: "case_status",
    label: "A case's status changes",
    caption: "The assistant reviews the case for what the new status calls for."
  },
  {
    kind: "intake_submitted",
    label: "The client submits an intake",
    caption: "The assistant reads and assesses the submission."
  },
  {
    kind: "vault_upload",
    label: "A document is uploaded to the client's vault",
    caption:
      "The assistant checks what it is and whether a case is waiting on it."
  },
  {
    kind: "expiration",
    label: "An expiration is approaching",
    caption:
      "Within 90 days, checked daily: status, EAD, advance parole, passport or visa."
  }
];

/** The wait the switch turns on. */
export const AUTO_ANSWER_MINUTES = 10;

type Settings = {
  askEveryWrite: boolean;
  autoAnswerMinutes: number | null;
  wakes: Partial<Record<AgentEventKind, boolean>>;
};

/** What the firm holds, read off the firm record. */
export const settingsOf = (
  firm: Pick<
    LawFirm,
    "agent_ask_every_write" | "agent_auto_answer_minutes" | "agent_event_wakes"
  > | null
): Settings => ({
  askEveryWrite: Boolean(firm?.agent_ask_every_write),
  autoAnswerMinutes: firm?.agent_auto_answer_minutes ?? null,
  wakes: { ...(firm?.agent_event_wakes ?? {}) }
});

const Divider = () => <div className="h-px w-full bg-fill-1" />;

const Row: FC<{
  label: string;
  caption: string;
  checked: boolean;
  disabled: boolean;
  testId: string;
  onChange: (on: boolean) => void;
  indent?: boolean;
}> = ({ label, caption, checked, disabled, testId, onChange, indent }) => (
  <div
    className={[
      "flex w-full items-center justify-between gap-6 py-3",
      indent ? "pl-10 pr-6" : "px-6"
    ].join(" ")}
  >
    <div className="flex min-w-0 flex-col items-start gap-1">
      <span className="text-body font-body text-default-font">{label}</span>
      <span className="text-caption font-caption text-subtext-color">
        {caption}
      </span>
    </div>
    <Switch
      aria-label={label}
      data-testid={testId}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onChange}
    />
  </div>
);

export const FirmApprovalsSection: FC<Props> = ({ lawFirmId, canEdit }) => {
  const [settings, setSettings] = useState<Settings>(settingsOf(null));
  const [loading, setLoading] = useState(Boolean(lawFirmId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lawFirmId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    LawFirmsAPI.getById(lawFirmId)
      .then((firm) => {
        if (cancelled) return;
        setSettings(settingsOf(firm));
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : "Could not load the firm's approval settings."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lawFirmId]);

  const disabled = !canEdit || saving || loading || !lawFirmId;

  const save = async (
    next: Settings,
    payload: Parameters<typeof LawFirmsAPI.update>[1]
  ) => {
    if (!lawFirmId) return;
    const previous = settings;
    setSettings(next);
    setSaving(true);
    try {
      await LawFirmsAPI.update(lawFirmId, payload);
      announceFirmUpdated(lawFirmId);
    } catch (err) {
      setSettings(previous);
      toast.error(
        err instanceof Error
          ? err.message
          : "Could not save the firm's approval settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const onAskEveryWrite = (on: boolean) =>
    void save(
      { ...settings, askEveryWrite: on },
      { agent_ask_every_write: on }
    );

  const onAutoAnswer = (on: boolean) => {
    const minutes = on ? AUTO_ANSWER_MINUTES : null;
    void save(
      { ...settings, autoAnswerMinutes: minutes },
      { agent_auto_answer_minutes: minutes }
    );
  };

  const onWake = (kind: AgentEventKind, on: boolean) => {
    const wakes = { ...settings.wakes, [kind]: on };
    void save({ ...settings, wakes }, { agent_event_wakes: wakes });
  };

  return (
    <section className="flex w-full flex-col" data-testid="firm-approvals">
      <div className="flex w-full items-center justify-between gap-6 px-6 pt-4 pb-1">
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-body font-body text-default-font">
            Approvals and events
          </span>
          <span className="text-caption font-caption text-subtext-color">
            What the assistant may do at your firm without being asked first.
            {canEdit ? "" : " Only firm owners and admins can change this."}
          </span>
        </div>
        {loading ? (
          <Loader2 className="size-4 shrink-0 animate-spin text-subtext-color" />
        ) : null}
      </div>
      {error ? (
        <p className="px-6 pb-2 text-caption font-caption text-destructive">
          {error}
        </p>
      ) : null}

      <Row
        label="Ask before every change"
        caption="Off: reversible changes run and can be undone from their change card; anything that reaches the client or cannot be undone still asks. On: every change asks first."
        checked={settings.askEveryWrite}
        disabled={disabled}
        testId="firm-ask-every-write"
        onChange={onAskEveryWrite}
      />
      <Divider />
      <Row
        label="Go ahead on Recommended answers"
        caption={`When someone has told the assistant not to ask first, a question it asks anyway goes with its Recommended answers after ${
          settings.autoAnswerMinutes ?? AUTO_ANSWER_MINUTES
        } minutes without a reply, and its reply says so. Questions without a Recommended answer still wait.`}
        checked={settings.autoAnswerMinutes !== null}
        disabled={disabled}
        testId="firm-auto-answer"
        onChange={onAutoAnswer}
      />
      <Divider />

      <div className="flex min-w-0 flex-col items-start gap-1 px-6 pt-3 pb-1">
        <span className="text-body font-body text-default-font">
          Wake the assistant on events
        </span>
        <span className="text-caption font-caption text-subtext-color">
          When one is on, the event posts in the client&apos;s channel and the
          assistant proposes next steps. Every proposal waits for approval
          there, and the client&apos;s assignees are notified.
        </span>
      </div>
      {EVENT_WAKE_ROWS.map((row) => (
        <Row
          key={row.kind}
          indent
          label={row.label}
          caption={row.caption}
          checked={Boolean(settings.wakes[row.kind])}
          disabled={disabled}
          testId={`event-wake-${row.kind}`}
          onChange={(on) => onWake(row.kind, on)}
        />
      ))}
    </section>
  );
};
